import React, { useState } from 'react';
import { requestAccess } from './api';

function App() {
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedOrg = orgName.trim();

    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }

    setSubmitting(true);

    try {
      await requestAccess({
        email: trimmedEmail,
        orgName: trimmedOrg || undefined
      });
      setStatus(
        'Request submitted. If approved, an administrator will contact you with a token.'
      );
      setEmail('');
      setOrgName('');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError(err?.message ?? 'Failed to submit request. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white/90 p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900/90">
        <div className="mb-4">
          <div className="text-sm font-semibold tracking-wide text-emerald-600 dark:text-emerald-400">
            NoPackageMalware
          </div>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Because npm install shouldn't feel like Russian roulette.
          </p>
          <h1 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-50">
            Request access token
          </h1>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Submit your email to request an access token for the secure npm registry. An
            administrator will review your request and contact you with further
            instructions.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
              Work email
            </label>
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-50 dark:placeholder:text-slate-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
              Organization (optional)
            </label>
            <input
              type="text"
              placeholder="Acme Corp"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-50 dark:placeholder:text-slate-500"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              If omitted, we'll infer it from your email domain when possible.
            </p>
          </div>

          {status && (
            <div className="rounded-md border border-emerald-500/50 bg-emerald-50/90 px-3 py-2 text-xs text-emerald-900 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
              {status}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-rose-500/60 bg-rose-50/90 px-3 py-2 text-xs text-rose-900 shadow-sm dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-100">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-emerald-50 shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Request access'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default App;
